import React, { useState, useContext, useEffect, useRef } from "react";
import { Table, Button } from "reactstrap";
import { FileMinus } from "lucide-react";
import { AppContext } from '../application/provider';
import Loader from "../components/Loader/Loader";
import { ButtonSearch } from '../components/Buttons/Buttons';
import ButtonPdf from '../components/Buttons/ButtonPdf';
import ButtonExcel from '../components/Buttons/ButtonExcel';
import ButtonUpdate from '../components/Buttons/ButtonUpdate';
import ButtonInsert from '../components/Buttons/ButtonInsert';
import ModalActualizarPagoAdministracion from '../components/Modal/ModalActualizarPagoAdministracion';
import ModalAgregarAdministracionCargoPago from '../components/Modal/ModalAgregarAdministracionCargoPago';
import "./CargoPago.css";

import AdministracionPlanillaService from "../axios_services/administracion_planilla.service";
import UsuariosService from "../axios_services/usuarios.service";
import SueldoEmpleadoService from "../axios_services/sueldoempleado.service";
import ArchivosService from "../axios_services/archivos.service";

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

function AdministracionCargoPago() {
    const [state] = useContext(AppContext);
    const [periodo_fecha, setPeriodoFecha] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });

    const [loading, setLoading] = useState(true);
    const [planillaData, setPlanillaData] = useState([]);
    const [validationStatus, setValidationStatus] = useState(null);
    const [actualizarPagoModal, setActualizarPagoModal] = useState(false);
    const [selectedItemPago, setSelectedItemPago] = useState(null);
    const [agregarModal, setAgregarModal] = useState(false);

    const allowedPerfiles = [1, 5, 9, 11, 21, 22, 26];

    const fetchDatos = async () => {
        setLoading(true);
        try {
            // 1. Fetch dependencies
            const usuariosReq = AdministracionPlanillaService.getUsuariosParaPlanilla().catch(() => []);
            const sueldosReq = SueldoEmpleadoService.getSueldosPerfil().catch(() => []);

            let descuentosData = [];
            try {
                descuentosData = await AdministracionPlanillaService.getDescuentosPlanillaResumen(periodo_fecha) || [];
            } catch (e) {
                console.warn("Could not fetch descuentos resumen, maybe API changes", e);
            }

            const dbPlanillaReq = AdministracionPlanillaService.getPlanillaAdministracion(periodo_fecha).catch(() => []);
            const boletasReq = ArchivosService.getBoletasPagoPeriodo(periodo_fecha).catch(() => []);

            const [usuariosFull, sueldos, dbPlanilla, boletasData] = await Promise.all([usuariosReq, sueldosReq, dbPlanillaReq, boletasReq]);

            // Safe extraction of arrays if they are wrapped in an object like {data: [...]} 
            const arrUsuarios = Array.isArray(usuariosFull) ? usuariosFull : (usuariosFull?.data || usuariosFull?.usuarios || Object.values(usuariosFull || {}) || []);
            const arrSueldos = Array.isArray(sueldos) ? sueldos : (sueldos?.data || sueldos?.sueldos || Object.values(sueldos || {}) || []);
            const arrDbPlanilla = Array.isArray(dbPlanilla) ? dbPlanilla : (dbPlanilla?.data || Object.values(dbPlanilla || {}) || []);
            const arrBoletas = Array.isArray(boletasData) ? boletasData : (boletasData?.data || []);

            // Filter users
            const usuariosFiltrados = arrUsuarios.filter(u => u && u.perfil_id && allowedPerfiles.includes(Number(u.perfil_id)) && Number(u.estado) === 1);

            // See validation status
            const validationRow = arrDbPlanilla.find(p => p && (p.aprobado_th || p.aprobado_sa || p.aprobado_gg)) || arrDbPlanilla[0];
            const isThAprob = validationRow ? validationRow.aprobado_th : 0;
            const isSaAprob = validationRow ? validationRow.aprobado_sa : 0;
            const isGgAprob = validationRow ? validationRow.aprobado_gg : 0;

            const getAreaName = (id) => {
                const a = state?.catalogos?.areas?.find(a => Number(a.area_id) === Number(id));
                return a ? a.descripcion : 'ADMINISTRACIÓN';
            };

            const computedData = usuariosFiltrados.map((u, i) => {
                // Descuento logic (Source of truth: Descuentos Planilla summary)
                const descObj = (descuentosData?.data || []).find(d => d.usuario_id === u.usuario_id);
                const currentDescuento = descObj ? parseFloat(descObj.total_descuento || 0) : 0;

                // Find in DB
                const dbRow = arrDbPlanilla.find(p => p && p.usuario_id === u.usuario_id);

                let rowData;
                if (dbRow) {
                    rowData = {
                        ...dbRow,
                        nombre_completo_usuario: u.apellidos + ' ' + (u.nombre || u.nombres || ''),
                        perfil_descripcion: getPerfilName(u.perfil_id),
                        area_descripcion: getAreaName(u.area_id),
                        dni: u.dni,
                        n_cuenta_bancaria: u.n_cuenta_bancaria,
                        descuento: parseFloat(parseFloat(dbRow.descuento || 0).toFixed(2))
                    };
                } else {
                    // Initial Calculations
                    const sueldoObj = arrSueldos.find(s => s && Number(s.perfil_id) === Number(u.perfil_id) && Number(s.estado) === 1);
                    const remuneracion = sueldoObj ? parseFloat(sueldoObj.monto_sueldo) : 0;

                    const bono_anual = 0;
                    const bono_gestion = 0;
                    const retencion = 0;

                    rowData = {
                        usuario_id: u.usuario_id,
                        nombre_completo_usuario: u.apellidos + ' ' + (u.nombre || u.nombres || ''),
                        perfil_descripcion: getPerfilName(u.perfil_id),
                        area_descripcion: getAreaName(u.area_id),
                        dni: u.dni,
                        n_cuenta_bancaria: u.n_cuenta_bancaria,
                        sueldo_perfil: remuneracion,
                        bono_anual,
                        bono_gestion: 0,
                        descuento: parseFloat(parseFloat(currentDescuento || 0).toFixed(2)),
                        vacaciones_truncas: 0,
                        total_aporte: 0, // Will be recalc below
                        retencion,
                        importe_neto: 0, // Will be recalc below
                        total_pagar_planilla: 0, // Will be recalc below
                        fecha_pago: null
                    };
                }

                if (dbRow) {
                    rowData.vacaciones_truncas = parseFloat(parseFloat(dbRow.vacaciones_truncas || 0).toFixed(2));
                }

                // Global Recalculation (to ensure Net is correct with new discount/vacaciones)
                const total_pagar = (rowData.sueldo_perfil - rowData.descuento) + parseFloat(rowData.bono_anual || 0) + parseFloat(rowData.bono_gestion || 0) + parseFloat(rowData.vacaciones_truncas || 0);

                let aporte_porcentaje = 0.1137; // Default
                if (Number(u.perfil_id) === 1) aporte_porcentaje = 0.1292;
                else if (Number(u.perfil_id) === 9) aporte_porcentaje = 0.13;

                rowData.total_pagar_planilla = parseFloat(total_pagar.toFixed(2));

                let temp_aporte = 0;
                // If the percentage was already saved, try to keep it, else use default
                if (dbRow && dbRow.total_pagar_planilla > 0) {
                    const savedPercentage = dbRow.total_aporte / dbRow.total_pagar_planilla;
                    temp_aporte = total_pagar * savedPercentage;
                } else {
                    temp_aporte = total_pagar * aporte_porcentaje;
                }
                
                rowData.total_aporte = parseFloat(temp_aporte.toFixed(2));
                rowData.importe_neto = parseFloat(((rowData.total_pagar_planilla - rowData.total_aporte) - parseFloat(rowData.retencion || 0)).toFixed(2));

                // Add shadow fields for change detection
                rowData.fecha_pago_db = rowData.fecha_pago;

                // Boleta de Pago info
                const boleta = arrBoletas.find(b => 
                    (Number(b.origen_id) === Number(u.usuario_id)) || 
                    (Number(b.usuario_id) === Number(u.usuario_id))
                );
                rowData.boleta_url = boleta ? boleta.url_completa : null;

                return rowData;
            });

            setPlanillaData(computedData);

            if (arrDbPlanilla.length > 0) {
                setValidationStatus({
                    aprobado_th: isThAprob,
                    aprobado_sa: isSaAprob,
                    aprobado_gg: isGgAprob
                });
            } else {
                setValidationStatus(null);
            }

        } catch (error) {
            console.error('Error fetching data:', error);
            setPlanillaData([]);
            setValidationStatus(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDatos();
    }, [state.catalogos]);

    const getPerfilName = (id) => {
        const p = state?.catalogos?.perfiles?.find(p => p.perfil_id === id);
        return p ? p.descripcion : 'N/A';
    };

    const handleSearch = () => {
        fetchDatos();
    };

    const handleSaveRow = async (formData) => {
        setLoading(true);
        try {
            await AdministracionPlanillaService.guardarPlanillaAdministracion({
                periodo_planilla: periodo_fecha,
                registros: [{
                    usuario_id: formData.usuario_id,
                    sueldo_perfil: formData.sueldo_perfil,
                    bono_anual: formData.bono_anual,
                    bono_gestion: formData.bono_gestion,
                    vacaciones_truncas: formData.vacaciones_truncas,
                    descuento: formData.descuento,
                    total_aporte: formData.total_aporte,
                    retencion: formData.retencion,
                    importe_neto: formData.importe_neto,
                    total_pagar_planilla: formData.total_pagar_planilla,
                    fecha_pago: null
                }]
            });
            alert('Registro guardado correctamente.');
            await fetchDatos();
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e, index, field) => {
        const val = parseFloat(e.target.value) || 0;
        const newData = [...planillaData];
        newData[index][field] = val;

        const row = newData[index];
        const total_pagar = (row.sueldo_perfil - row.descuento) + parseFloat(row.bono_anual || 0) + parseFloat(row.bono_gestion || 0) + parseFloat(row.vacaciones_truncas || 0);

        let perfil_id = null;
        const uInfo = state?.catalogos?.perfiles?.find(p => p.descripcion === row.perfil_descripcion);
        if (uInfo) perfil_id = uInfo.perfil_id; // Just approx logic if real user perfil missing

        // Since we filtered originally, we can just approximate from percentage
        let aporte = 0;
        if (total_pagar > 0) {
            // simple recalculate using previous percentage ratio to avoid needing original user object
            const percentage = row.total_aporte > 0 ? (row.total_aporte / row.total_pagar_planilla) : 0.1137;
            aporte = total_pagar * percentage;
        }

        row.total_pagar_planilla = parseFloat(total_pagar.toFixed(2));
        row.total_aporte = parseFloat(aporte.toFixed(2));
        row.importe_neto = parseFloat(((row.total_pagar_planilla - row.total_aporte) - parseFloat(row.retencion || 0)).toFixed(2));

        setPlanillaData(newData);
    };

    const handleAprobar = async () => {
        if (!window.confirm("¿Está seguro de que desea aprobar y firmar la planilla para este periodo? Esta acción guardará los datos actuales y registrará su validación oficial.")) {
            return;
        }
        setLoading(true);
        try {
            // Se guardan los datos (incluyendo bonos si el perfil lo permite)
            const payload = {
                periodo_planilla: periodo_fecha,
                registros: planillaData.map(d => ({
                    usuario_id: d.usuario_id,
                    sueldo_perfil: d.sueldo_perfil,
                    bono_anual: d.bono_anual,
                    bono_gestion: d.bono_gestion,
                    vacaciones_truncas: d.vacaciones_truncas,
                    descuento: d.descuento,
                    total_aporte: d.total_aporte,
                    retencion: d.retencion,
                    importe_neto: d.importe_neto,
                    fecha_pago: d.fecha_pago
                }))
            };
            await AdministracionPlanillaService.guardarPlanillaAdministracion(payload);

            // Se procede a realizar la validación (firma) según el perfil del usuario
            const perfilId = Number(state.user.perfil_id);
            await AdministracionPlanillaService.validarPlanillaAdministracion(periodo_fecha, perfilId);

            alert("Planilla guardada y firmada/validada correctamente.");
            await fetchDatos();
        } catch (error) {
            alert("Error al procesar la aprobación: " + (error.response?.data?.error || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleValidar = async () => {
        setLoading(true);
        try {
            await AdministracionPlanillaService.validarPlanillaAdministracion(periodo_fecha, Number(state.user.perfil_id));
            alert("Planilla validada correctamente.");
            await fetchDatos();
        } catch (error) {
            alert("Error al validar planilla: " + (error.response?.data?.error || error.message));
        } finally {
            setLoading(false);
        }
    };

    const formatSoles = (val) => new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(val || 0);
    const canEditBonuses = [5, 8].includes(Number(state.user?.perfil_id)) && Number(validationStatus?.aprobado_th) !== 1;
    const canEditPaymentDate = [8, 26].includes(Number(state.user?.perfil_id)) && Number(validationStatus?.aprobado_gg) === 1;
    const canUploadBoleta = [5, 8, 22].includes(Number(state.user?.perfil_id));
    const canUpdatePago = [5, 8, 22, 26].includes(Number(state.user?.perfil_id));
    const totalImporteNeto = planillaData.reduce((sum, row) => sum + (Number(row.importe_neto) || 0), 0);

    const getStatusText = () => {
        if (!validationStatus) return "PENDIENTE";
        if (Number(validationStatus.aprobado_gg) === 1) return "APROBADO POR GERENCIA GENERAL";
        if (Number(validationStatus.aprobado_sa) === 1) return "APROBADO POR SUBGERENCIA ADMINISTRATIVA";
        if (Number(validationStatus.aprobado_th) === 1) return "APROBADO POR TALENTO HUMANO";
        return "PENDIENTE";
    };

    const isApprovalDisabled = () => {
        if (!state.user) return true;
        const perfil = Number(state.user.perfil_id);
        const { aprobado_th, aprobado_sa, aprobado_gg } = validationStatus || {};

        if ([5, 8].includes(perfil)) return Number(aprobado_th) === 1;
        if (perfil === 26) return Number(aprobado_th) !== 1 || Number(aprobado_sa) === 1;
        if (perfil === 9) return Number(aprobado_sa) !== 1 || Number(aprobado_gg) === 1;
        return true;
    };

    const exportToPDF = async () => {
        const doc = new jsPDF('landscape', 'pt', 'a4');
        const getMonthName = (YYYY_MM) => {
            if (!YYYY_MM) return '';
            const [y, m] = YYYY_MM.split('-');
            const date = new Date(Number(y), Number(m) - 1, 1);
            return date.toLocaleString('es-PE', { month: 'long', year: 'numeric' }).toUpperCase();
        };
        const monthYearStr = getMonthName(periodo_fecha);

        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("MEC BUSINNES PARTNER S.A.C.", 28, 30);
        doc.text("20612796484", 28, 45);

        const tableColumnNames = [
            "N°", "AREA", "APELLIDOS Y NOMBRES", "CARGO", "REMUNERACIÓN", "BONO ANUAL", "BONO DE GESTION",
            "LIQUIDACION DE\nVACACIONES TRUNCAS", "DESCUENTO", "TOTAL A PAGAR PLANILLA", "TOTAL\nAPORT DEL\nTRABAJADOR\n(AFP / ONP)",
            "RETENCIÓN 5TA CATE", "IMPORTE NETO A PAGAR", "N° DNI", "N° CUENTA", "BANCO", "FECHA DE PAGO"
        ];

        const formatNumber = (val) => {
            if (val === 0 || !val) return "S/ 0.00";
            return new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN', minimumFractionDigits: 2 }).format(val);
        };

        const tableRows = planillaData.map((row, index) => [
            index + 1,
            row.area_descripcion?.toUpperCase() || 'ADMINISTRATIVO',
            row.nombre_completo_usuario?.toUpperCase(),
            row.perfil_descripcion?.toUpperCase(),
            formatNumber(row.sueldo_perfil),
            formatNumber(row.bono_anual),
            formatNumber(row.bono_gestion),
            formatNumber(row.vacaciones_truncas),
            formatNumber(row.descuento),
            formatNumber(row.total_pagar_planilla),
            formatNumber(row.total_aporte),
            formatNumber(row.retencion),
            formatNumber(row.importe_neto),
            row.dni,
            row.n_cuenta_bancaria || '',
            "INTERBANK",
            row.fecha_pago || "" // Fecha de Pago
        ]);

        // Agregar fila de total
        const totalRow = Array(17).fill("");
        totalRow[12] = formatNumber(totalImporteNeto);
        tableRows.push(totalRow);

        autoTable(doc, {
            startY: 60,
            head: [
                [{
                    content: `CARGO DE PAGO PLANILLA DE ${monthYearStr} - PERSONAL 5TA CATEGORIA - ADMINISTRACION`,
                    colSpan: 17,
                    styles: { halign: 'center', fillColor: [42, 114, 42], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9, lineColor: [0, 0, 0], lineWidth: 0.5 }
                }],
                tableColumnNames
            ],
            body: tableRows,
            theme: 'grid',
            styles: { fontSize: 6, cellPadding: 2, overflow: 'linebreak', halign: 'center', valign: 'middle', lineColor: [0, 0, 0], lineWidth: 0.5 },
            margin: { left: 25, right: 15 },
            headStyles: { fillColor: [255, 192, 0], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 6, lineColor: [0, 0, 0], lineWidth: 0.5 },
            columnStyles: {
                0: { cellWidth: 15 }, // Nº
                1: { halign: 'left', cellWidth: 60, cellPadding: { left: 5, right: 2, top: 2, bottom: 2 } }, // AREA
                2: { halign: 'left', cellWidth: 70, cellPadding: { left: 5, right: 2, top: 2, bottom: 2 } }, // NOMBRES 
                3: { halign: 'left', cellWidth: 60, cellPadding: { left: 5, right: 2, top: 2, bottom: 2 } }, // CARGO
                4: { cellWidth: 60 }, // REMUNERACION 
                5: { cellWidth: 35 }, // BONO ANUAL
                6: { cellWidth: 35 }, // BONO GESTION
                7: { cellWidth: 65 }, // LIQUIDACION DE VACACIONES TRUNCAS
                8: { cellWidth: 50 }, // Descuento
                9: { cellWidth: 40 }, // TOTAL A PAGAR PLANILLA
                10: { cellWidth: 40 }, // TOTAL APORT
                11: { cellWidth: 40 }, // RETENCION
                12: { cellWidth: 40 }, // IMPORTE NETO A PAGAR
                13: { cellWidth: 35 }, // DNI
                14: { cellWidth: 60, cellPadding: { left: 5, right: 5, top: 2, bottom: 2 } }, // CUENTA
                15: { cellWidth: 40 }, // BANCO
                16: { cellWidth: 40 }, // Fecha de pago
            },
            didParseCell: function (data) {
                if (data.section === 'head' && data.row.index === 1) {
                    if (data.column.index === 8) { // Descuento
                        data.cell.styles.fillColor = [255, 0, 0];
                        data.cell.styles.textColor = [255, 255, 255];
                    }
                    if (data.column.index === 12) { // IMPORTE NETO A PAGAR
                        data.cell.styles.fillColor = [254, 254, 0]; // Amarillo #fefe00
                    }
                    if (data.column.index === 16) { // Fecha de pago
                        data.cell.styles.fillColor = [252, 213, 180];
                    }
                }

                // Formato para la última fila de total
                if (data.section === 'body' && data.row.index === tableRows.length - 1) {
                    if (data.column.index === 12) { // Celda de Importe Neto (Total)
                        data.cell.styles.fontStyle = 'bold';
                        data.cell.styles.halign = 'right';
                        data.cell.styles.fillColor = [255, 255, 255]; // Fondo blanco
                        data.cell.styles.lineWidth = 0.5; // Mantener borde completo
                    } else if (data.column.index === 13) { // La celda a la derecha del total
                        data.cell.styles.fillColor = [255, 255, 255];
                        data.cell.styles.lineWidth = { top: 0.5, right: 0, bottom: 0, left: 0.5 }; // Borde izquierdo forzado
                        data.cell.styles.textColor = [255, 255, 255];
                    } else {
                        data.cell.styles.fillColor = [255, 255, 255];
                        data.cell.styles.lineWidth = { top: 0.5, right: 0, bottom: 0, left: 0 }; // Top border only
                        data.cell.styles.textColor = [255, 255, 255]; // Ocultar texto
                    }
                }
            }
        });

        const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY : 100;
        let startY = finalY + 110;

        if (startY > doc.internal.pageSize.height - 70) {
            doc.addPage();
            startY = 70;
        }

        try {
            /*
            // =========================================================================
            // CODIGO ANTERIOR (3 FIRMAS CON TALENTO HUMANO / PERFIL 5)
            // Para reactivar cuando el perfil 5 vuelva a operar con jefatura independiente:
            // =========================================================================
            // const [firmaThRes, firmaGgRes, firmaSaRes] = await Promise.all([
            //     ArchivosService.getFirmaDigitalConvenio(88).catch(() => null),
            //     ArchivosService.getFirmaDigitalConvenio(132).catch(() => null),
            //     ArchivosService.getFirmaDigitalConvenio(251).catch(() => null)
            // ]);
            // const firmaThB64 = firmaThRes?.base64 || null;
            // const firmaGgB64 = firmaGgRes?.base64 || null;
            // const firmaSaB64 = firmaSaRes?.base64 || null;
            // const pageWidth = doc.internal.pageSize.width;
            // const center1 = pageWidth * 0.25;
            // const center2 = pageWidth * 0.50;
            // const center3 = pageWidth * 0.75;
            // doc.setFontSize(7);
            // doc.setTextColor(0, 0, 0);
            // const drawSignature = (b64, name, role, cx, y) => {
            //     if (b64) {
            //         const format = b64.includes('image/jpeg') || b64.includes('image/jpg') || b64.includes('data:image/jpeg') ? 'JPEG' : 'PNG';
            //         doc.addImage(b64, format, cx - 50, y - 50, 100, 50);
            //     }
            //     doc.setLineWidth(0.5);
            //     doc.setDrawColor(0, 0, 0);
            //     doc.line(cx - 75, y, cx + 75, y);
            //     doc.setFont("helvetica", "bold");
            //     doc.text(role, cx, y + 12, { align: 'center' });
            //     doc.setFont("helvetica", "normal");
            //     doc.text(name, cx, y + 22, { align: 'center' });
            // };
            // drawSignature(firmaGgB64, "RAMIREZ TAIPE MANUEL ANGEL", "GERENTE GENERAL", center1, startY);
            // drawSignature(firmaSaB64, "ROSALES SAMANIEGO GABRIELA PATRICIA", "SUB GERENTE DE ADMINISTRACIÓN", center2, startY);
            // drawSignature(firmaThB64, "CHIPANA GAGO FLERIDA DANIELA", "JEFE DE TALENTO HUMANO", center3, startY);
            // =========================================================================
            */

            // CONFIGURACIÓN ACTUAL (ADMINISTRADO POR SUB GERENCIA DE ADMINISTRACIÓN - PERFIL 26)
            const [firmaGgRes, firmaSaRes] = await Promise.all([
                ArchivosService.getFirmaDigitalConvenio(132).catch(() => null),
                ArchivosService.getFirmaDigitalConvenio(251).catch(() => null)
            ]);

            const firmaGgB64 = firmaGgRes?.base64 || null;
            const firmaSaB64 = firmaSaRes?.base64 || null;

            const pageWidth = doc.internal.pageSize.width;
            const center1 = pageWidth * 0.35;
            const center2 = pageWidth * 0.65;

            doc.setFontSize(7);
            doc.setTextColor(0, 0, 0);

            const drawSignature = (b64, name, role, cx, y) => {
                if (b64) {
                    const format = b64.includes('image/jpeg') || b64.includes('image/jpg') || b64.includes('data:image/jpeg') ? 'JPEG' : 'PNG';
                    doc.addImage(b64, format, cx - 50, y - 50, 100, 50); // Larger image
                }
                doc.setLineWidth(0.5);
                doc.setDrawColor(0, 0, 0);
                doc.line(cx - 75, y, cx + 75, y); // Wider line (150 width)

                doc.setFont("helvetica", "bold");
                doc.text(role, cx, y + 12, { align: 'center' });

                doc.setFont("helvetica", "normal");
                doc.text(name, cx, y + 22, { align: 'center' });
            };

            drawSignature(firmaSaB64, "ROSALES SAMANIEGO GABRIELA PATRICIA", "SUB GERENTE DE ADMINISTRACIÓN", center1, startY);
            drawSignature(firmaGgB64, "RAMIREZ TAIPE MANUEL ANGEL", "GERENTE GENERAL", center2, startY);

        } catch (error) {
            console.error("Error cargando firmas:", error);
        }

        doc.save(`Cargo_Pago_Administracion_${periodo_fecha}.pdf`);
    };

    const exportToExcel = async () => {
        const getMonthName = (YYYY_MM) => {
            if (!YYYY_MM) return '';
            const [y, m] = YYYY_MM.split('-');
            const date = new Date(Number(y), Number(m) - 1, 1);
            return date.toLocaleString('es-PE', { month: 'long', year: 'numeric' }).toUpperCase();
        };
        const monthYearStr = getMonthName(periodo_fecha);

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Planilla Administracion');

        // Header company info
        worksheet.getCell('A1').value = 'MEC BUSINNES PARTNER S.A.C.';
        worksheet.getCell('A1').font = { bold: true, size: 12 };
        worksheet.getCell('A2').value = '20612796484';
        worksheet.getCell('A2').font = { bold: true, size: 11 };

        // Main colored header
        worksheet.mergeCells('A4:Q4');
        const mainHeaderCell = worksheet.getCell('A4');
        mainHeaderCell.value = `CARGO DE PAGO PLANILLA DE ${monthYearStr} - PERSONAL 5TA CATEGORIA - ADMINISTRACION`;
        mainHeaderCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2A722A' } };
        mainHeaderCell.font = { color: { argb: 'FFFFFFFF' }, bold: true, size: 12 };
        mainHeaderCell.alignment = { horizontal: 'center', vertical: 'middle' };
        mainHeaderCell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        worksheet.getRow(4).height = 25;

        // Columns definitions
        worksheet.getRow(5).values = [
            "N°", "AREA", "APELLIDOS Y NOMBRES", "CARGO", "REMUNERACIÓN", "BONO ANUAL", "BONO DE GESTION",
            "LIQUIDACION DE VACACIONES TRUNCAS", "DESCUENTO", "TOTAL A PAGAR PLANILLA", "TOTAL\nAPORT DEL\nTRABAJADOR\n(AFP / ONP)",
            "RETENCIÓN 5TA CATE", "IMPORTE NETO A PAGAR", "N° DNI", "N° CUENTA", "BANCO", "Fecha de Pago"
        ];

        const headerRow = worksheet.getRow(5);
        headerRow.font = { bold: true, size: 9 };
        headerRow.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        headerRow.height = 40;

        // Columns background colors
        for (let idx = 1; idx <= 17; idx++) {
            const cell = headerRow.getCell(idx);
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

            if (idx === 9) { // Descuento
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF0000' } };
                cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
            } else if (idx === 17) { // Fecha de pago
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFCD5B4' } };
            } else if (idx === 13) { // Importe Neto (Amarillo más oscuro)
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFCC00' } };
            } else {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFC000' } };
            }
        }

        // Column Widths
        worksheet.columns = [
            { width: 5 }, { width: 22 }, { width: 32 }, { width: 30 }, { width: 20 }, { width: 14 }, { width: 14 },
            { width: 16 }, { width: 16 }, { width: 20 }, { width: 15 }, { width: 20 }, { width: 20 }, { width: 14 },
            { width: 20 }, { width: 14 }, { width: 15 }
        ];

        // Format number helper
        const curFormat = '"S/"#,##0.00';

        // Add Data
        planillaData.forEach((row, index) => {
            const r = worksheet.addRow([
                index + 1,
                row.area_descripcion?.toUpperCase() || 'ADMINISTRATIVO',
                row.nombre_completo_usuario?.toUpperCase(),
                row.perfil_descripcion?.toUpperCase(),
                row.sueldo_perfil,
                row.bono_anual,
                row.bono_gestion,
                row.vacaciones_truncas,
                row.descuento,
                row.total_pagar_planilla,
                row.total_aporte,
                row.retencion,
                row.importe_neto,
                row.dni,
                row.n_cuenta_bancaria || '',
                "INTERBANK",
                row.fecha_pago || "" // Fecha de Pago
            ]);

            // Apply borders and number formats
            r.eachCell((cell, colNumber) => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                cell.font = { size: 9 };
                if (colNumber >= 5 && colNumber <= 13) {
                    cell.numFmt = curFormat;
                }
            });
        });

        // Add Total Row
        const totalRow = worksheet.addRow([]);
        [5, 6, 7, 8, 9, 10, 11, 12, 13].forEach(colNum => {
            const cell = totalRow.getCell(colNum);
            cell.numFmt = '"S/ "#,##0.00';
            cell.alignment = { vertical: 'middle', horizontal: 'right' };
            cell.border = {
                top: { style: 'thin', color: { argb: 'FF000000' } },
                left: { style: 'thin', color: { argb: 'FF000000' } },
                bottom: { style: 'thin', color: { argb: 'FF000000' } },
                right: { style: 'thin', color: { argb: 'FF000000' } }
            };
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `Cargo_Pago_Administracion_${periodo_fecha}.xlsx`);
    };

    const renderValidationStatus = () => {
        return null; // El usuario ya no quiere este mensaje ni el botón aquí debajo
    };


    return (
        <div className="cargo-pago-module">
            {loading && <Loader />}
            <div className="flex items-center justify-between mb-4">
                <h1>CARGO DE PAGO PLANILLA - ADMINISTRACIÓN</h1>
                <div className="flex gap-2">
                    <ButtonPdf onClick={exportToPDF} disabled={loading || planillaData.length === 0 || Number(validationStatus?.aprobado_gg) !== 1} />
                    <ButtonExcel onClick={exportToExcel} disabled={loading || planillaData.length === 0} />
                </div>
            </div>
            <div className="filtros">
                <div>
                    <label htmlFor="periodo_fecha">Periodo de fecha:</label>
                    <input
                        id="periodo_fecha"
                        type="month"
                        value={periodo_fecha}
                        onChange={(e) => setPeriodoFecha(e.target.value)}
                    />
                </div>
            </div>

            <div className="flex flex-col gap-4 mb-4">
                <div className="flex justify-start items-center gap-3">
                    <ButtonSearch onClick={handleSearch} isLoading={loading} />
                </div>
                {planillaData.length > 0 && (
                    <div className="flex items-center justify-between">
                        <div style={{
                            backgroundColor: '#f5f5f5',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            border: '1px solid #e0e0e0'
                        }}>
                            <span className="text-sm text-gray-700 uppercase tracking-wider" style={{ fontWeight: '400' }}>
                                {getStatusText()}
                            </span>
                        </div>
                        <div className="flex items-center gap-3">
                            <Button
                                className={`px-4 py-2 rounded-lg text-[13px] font-semibold ${
                                    isApprovalDisabled()
                                        ? "bg-[#0b4d17] text-white opacity-95 cursor-not-allowed border-none shadow-none"
                                        : "bg-green-600 hover:bg-green-700 text-white border-none"
                                    }`}
                                onClick={handleAprobar}
                                disabled={isApprovalDisabled()}
                            >
                                Aprobar y firmar
                            </Button>
                            {[5, 8, 22].includes(Number(state.user?.perfil_id)) && (
                                <ButtonInsert onClick={() => setAgregarModal(true)} />
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className="table-container overflow-x-auto">
                <Table bordered responsive size="sm" className="text-xs">
                    <thead className="bg-[#14233a] text-white text-center align-middle">
                        <tr className="leading-tight">
                            <th className="p-1 text-center" style={{ width: '50px' }}>N°</th>
                            <th className="whitespace-nowrap p-1">APELLIDOS Y NOMBRE</th>
                            <th className="whitespace-nowrap p-1 min-w-[250px]">CARGO</th>
                            <th className="p-1 text-center" style={{ width: '130px', minWidth: '130px', maxWidth: '130px' }}>RENUMERACIÓN</th>
                            <th className="p-1 text-center leading-tight" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', wordBreak: 'break-word' }}>BONO<br />ANUAL</th>
                            <th className="p-1 min-w-[100px] text-center">BONO DE<br />GESTIÓN</th>
                            <th className="p-1 min-w-[160px] text-center">LIQUIDACION DE<br />VACACIONES TRUNCAS</th>
                            <th className="p-1 min-w-[80px]">DESCUENTO</th>
                            <th className="p-1 min-w-[120px] text-center">TOTAL A PAGAR<br />PLANILLA</th>
                            <th className="p-1 min-w-[120px] text-center">TOTAL APORTE<br />TRABAJADOR</th>
                            <th className="p-1 min-w-[110px] text-center">RETENCIÓN<br />5TA CATE</th>
                            <th className="p-1 min-w-[120px] text-center">IMPORTE NETO<br />A PAGAR</th>
                            <th className="Whitespace-nowrap p-1">DNI</th>
                            <th className="whitespace-nowrap p-1">CUENTA</th>
                            <th className="p-1">BANCO</th>
                            <th className="p-1" style={{ width: '120px', minWidth: '120px' }}>BOLETA DE<br />PAGO</th>
                            <th className="p-1 min-w-[130px]">ESTADO<br />PAGO</th>
                            <th className="p-1" style={{ width: '150px', minWidth: '150px' }}>FECHA DE PAGO</th>
                            {canUpdatePago && <th className="p-1 text-center min-w-[100px]">ACCIONES</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {planillaData.length > 0 ? planillaData.map((row, index) => (
                            <tr key={row.usuario_id} className="align-middle">
                                <td className="py-1">{index + 1}</td>
                                <td className="py-1">{row.nombre_completo_usuario?.toUpperCase()}</td>
                                <td className="py-1">{row.perfil_descripcion}</td>
                                <td className="py-1 text-center" style={{ width: '130px', minWidth: '130px', maxWidth: '130px' }}>{formatSoles(row.sueldo_perfil)}</td>
                                <td className="py-1 text-center" style={{ width: '120px', minWidth: '120px', maxWidth: '120px' }}>
                                    {canEditBonuses ? (
                                        <div className="flex items-center justify-center gap-1">
                                            <span className="text-xs text-gray-500">S/</span>
                                            <input
                                                type="number"
                                                value={row.bono_anual === 0 ? "0.00" : row.bono_anual || ''}
                                                step="0.01"
                                                onChange={(e) => handleInputChange(e, index, 'bono_anual')}
                                                className="w-20 form-control form-control-sm h-7 text-center px-1"
                                            />
                                        </div>
                                    ) : (
                                        <span>{formatSoles(row.bono_anual)}</span>
                                    )}
                                </td>
                                <td className="py-1 text-center">
                                    {canEditBonuses ? (
                                        <div className="flex items-center justify-center gap-1">
                                            <span className="text-xs text-gray-500">S/</span>
                                            <input
                                                type="number"
                                                value={row.bono_gestion === 0 ? "0.00" : row.bono_gestion || ''}
                                                step="0.01"
                                                onChange={(e) => handleInputChange(e, index, 'bono_gestion')}
                                                className="w-20 form-control form-control-sm h-7 text-center px-1"
                                            />
                                        </div>
                                    ) : (
                                        <span>{formatSoles(row.bono_gestion)}</span>
                                    )}
                                </td>
                                <td className="py-1 text-center">
                                    {canEditBonuses ? (
                                        <div className="flex items-center justify-center gap-1">
                                            <span className="text-xs text-gray-500">S/</span>
                                            <input
                                                type="number"
                                                value={row.vacaciones_truncas === 0 ? "0.00" : row.vacaciones_truncas || ''}
                                                step="0.01"
                                                onChange={(e) => handleInputChange(e, index, 'vacaciones_truncas')}
                                                className="w-20 form-control form-control-sm h-7 text-center px-1"
                                            />
                                        </div>
                                    ) : (
                                        <span>{formatSoles(row.vacaciones_truncas)}</span>
                                    )}
                                </td>
                                <td className="bg-red-50 text-red-600 py-1 font-semibold">{formatSoles(row.descuento)}</td>
                                <td className="py-1">{formatSoles(row.total_pagar_planilla)}</td>
                                <td className="text-red-700 py-1">{formatSoles(row.total_aporte)}</td>
                                <td className="py-1 text-center">
                                    {canEditBonuses ? (
                                        <div className="flex items-center justify-center gap-1">
                                            <span className="text-xs text-gray-500">S/</span>
                                            <input
                                                type="number"
                                                value={row.retencion === 0 ? "0.00" : row.retencion || ''}
                                                step="0.01"
                                                onChange={(e) => handleInputChange(e, index, 'retencion')}
                                                className="w-20 form-control form-control-sm h-7 text-center px-1"
                                            />
                                        </div>
                                    ) : (
                                        <span>{formatSoles(row.retencion)}</span>
                                    )}
                                </td>
                                <td className="bg-yellow-100 py-1">{formatSoles(row.importe_neto)}</td>
                                <td className="py-1">{row.dni}</td>
                                <td className="py-1">{row.n_cuenta_bancaria || 'N/A'}</td>
                                <td className="py-1">INTERBANK</td>
                                <td className="py-1 text-center" style={{ width: '120px', minWidth: '120px' }}>
                                    <div className="flex items-center justify-center">
                                        <button
                                         onClick={() => {
                                             if (row.boleta_url) window.open(row.boleta_url, '_blank');
                                         }}
                                         className="p-1 rounded"
                                         style={{ cursor: row.boleta_url ? 'pointer' : 'default' }}
                                         title={row.boleta_url ? "Ver Boleta de Pago" : "Sin boleta disponible"}
                                     >
                                         <FileMinus
                                             size={20}
                                             style={{
                                                 color: row.boleta_url ? '#3cb167' : '#f87171',
                                                 opacity: row.boleta_url ? 1 : 0.4
                                             }}
                                         />
                                     </button>
                                    </div>
                                </td>
                                <td className="py-1 text-center min-w-[130px]">
                                    {state.catalogos?.tipos?.find(t => t.categoria_id == 32 && String(t.tipo_id) === String(row.estado_pago_id))?.descripcion || '—'}
                                </td>
                                <td className="py-1 text-center" style={{ width: '150px', minWidth: '150px' }}>
                                    <span style={{ whiteSpace: 'nowrap' }}>{row.fecha_pago || '—'}</span>
                                </td>
                                {canUpdatePago && (
                                    <td className="py-1 text-center">
                                        <div className="flex justify-center items-center">
                                            <ButtonUpdate
                                                onClick={() => { setSelectedItemPago(row); setActualizarPagoModal(true); }}
                                                title="Actualizar Pago y Boleta"
                                            />
                                        </div>
                                    </td>
                                )}
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan="16" className="text-center py-4 bg-white text-gray-500">No hay información de planillas administrativas.</td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr className="font-bold text-[#14233a] border-0">
                            <td colSpan="11" className="border-0 bg-transparent"></td>
                            <td className="bg-yellow-200 py-1 px-2 text-left border border-gray-300">
                                {formatSoles(totalImporteNeto)}
                            </td>
                            <td colSpan="5" className="border-0 bg-transparent"></td>
                        </tr>
                    </tfoot>
                </Table>
            </div>
            <ModalActualizarPagoAdministracion
                isOpen={actualizarPagoModal}
                onClose={() => { setActualizarPagoModal(false); setSelectedItemPago(null); }}
                onRefresh={fetchDatos}
                item={selectedItemPago}
                periodo={periodo_fecha}
            />
            <ModalAgregarAdministracionCargoPago
                isOpen={agregarModal}
                onClose={() => setAgregarModal(false)}
                onSave={handleSaveRow}
            />
        </div>
    );
}

export default AdministracionCargoPago;
